import { keys } from 'essential-utils';

import { Api, isDev, isLocal, isStage } from './api';
import { injectTSDI } from './tsdi';

export type Tenant = 'rsg-group' | 'levelx' | 'goldensquare';

export type Feature =
    | 'IS_DEV'
    | 'LEADS_PAGE'
    | 'ANNUAL_CALCULATION'
    | 'CREDIT_CARD'
    | 'LANDING_PAGES'
    | 'UK_ADDRESS_FORMAT'
    | 'NEW_SIGNATURE_FIELD'
    | 'AU_ADDRESS_FORMAT'
    | 'IE_ADDRESS_FORMAT'
    | 'UPC_INTEGRATION'
    | 'REDUCED_HEADER_WITHOUT_IMAGE';

type FeatureFlags = {
    [key in Feature]:
        | ({ [key in Tenant]?: boolean } & { '*': boolean })
        | boolean;
};

const featuresProd: FeatureFlags = {
    IS_DEV: false,
    LEADS_PAGE: false,
    ANNUAL_CALCULATION: false,
    CREDIT_CARD: true,
    LANDING_PAGES: true,
    UK_ADDRESS_FORMAT: true,
    NEW_SIGNATURE_FIELD: true,
    AU_ADDRESS_FORMAT: true,
    IE_ADDRESS_FORMAT: true,
    UPC_INTEGRATION: { '*': false, levelx: true, goldensquare: true },
    REDUCED_HEADER_WITHOUT_IMAGE: false
};

const featuresStage: FeatureFlags = {
    ...featuresProd,
    NEW_SIGNATURE_FIELD: true
};

const featuresDev: FeatureFlags = {
    ...enableFeatureFlags(featuresProd),
    LEADS_PAGE: false,
    ANNUAL_CALCULATION: false
};

function getFeatureEnv(): FeatureFlags {
    if (isDev()) {
        return featuresDev;
    }
    if (isStage()) {
        return featuresStage;
    }

    if (isLocal()) {
        return featuresDev;
    }

    return featuresProd;
}

export function featureEnabled(feature: Feature): boolean {
    const tenant = injectTSDI(Api)?.tenantName;
    const featureEnv = getFeatureEnv();
    const featureFlag = featureEnv[feature];

    if (typeof featureFlag === 'undefined') {
        console.warn(
            `Feature-flag '${feature}' does not exist. Non-existing feature-flags are enabled by default.`
        );

        return true;
    }

    if (typeof featureFlag === 'boolean') {
        return featureFlag;
    }

    const tenantFeatureFlag = featureFlag[tenant as Tenant];
    if (tenantFeatureFlag !== undefined) {
        return Boolean(tenantFeatureFlag);
    }

    const globalFeatureFlag = featureFlag['*'];
    if (globalFeatureFlag !== undefined) {
        return Boolean(globalFeatureFlag);
    }

    return true;
}

function enableFeatureFlags(features: FeatureFlags): FeatureFlags {
    return keys(features).reduce<FeatureFlags>(
        (memo, key) => ({ ...memo, [key]: true }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
        {} as any
    );
}
